import { useTenderActionError } from "../../../../../../../hooks/useTenderActionError";
import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Aviation } from "../../../../../../../services";
import CustomInputCurrency from "../../../../../../custom/custom-input-currency/CustomInputCurrency";
import CustomButton from "../../../../../../custom/custom-button/CustomButton";
import CloseIconNew from "../../../../../../shared/icons/CloseIconNew";
import style from "./ExpeditorUpdateBidModal.module.scss";

const ExpeditorUpdateBidModal = ({ onRefreshOrder, isOpen, onClose, bid, order, onSuccess }) => {
  const { t } = useTranslation();
  const { requestErrorDisplay } = useTenderActionError(order, { onRefreshOrder });
  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState("EUR");
  const [errorText, setErrorText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const lockCurrency = Boolean(order?.acceptedCurrency);

  useEffect(() => {
    if (!isOpen) return;
    setPrice("");
    setCurrency(order?.acceptedCurrency || bid?.currency || order?.currency || "EUR");
    setErrorText("");
  }, [bid?.currency, bid?.id, isOpen, order?.acceptedCurrency, order?.currency]);

  if (!isOpen) return null;

  const handleClose = () => {
    setPrice("");
    setErrorText("");
    onClose?.();
  };

  const handleUpdate = async () => {
    const roundedPrice = Math.round(Number(price));
    if (!roundedPrice || roundedPrice <= 0) {
      setErrorText(t("orderInfoExpeditor.bid.correctBidRequired"));
      return;
    }
    if (roundedPrice > 2000000000) {
      setErrorText(t("orderInfoExpeditor.bid.bidTooLarge"));
      return;
    }

    setIsLoading(true);
    try {
      await Aviation.updateSuggestedPrice(bid.id, {
        orderId: bid.orderId,
        price: roundedPrice,
        currency: order?.acceptedCurrency || currency,
        comments: bid.comments,
        weight: bid.weight,
        volume: bid.volume,
        plannedDays: bid.plannedDays,
        isExtraCosts: Boolean(bid.isExtraCosts),
      });
      toast.success(t("orderInfoExpeditor.bid.bidUpdated"));
      onSuccess?.();
    } catch (error) {
      console.error(error);
      await requestErrorDisplay(error, t, "orderInfoExpeditor.bid.updateBidError");
    } finally {
      setIsLoading(false);
    }
  };

  return ReactDOM.createPortal(
    <div className={style.overlay} onClick={handleClose} data-portal-modal="true">
      <div className={style.modal} onClick={(event) => event.stopPropagation()}>
        <div className={style.header}>
          <span className={style.title}>{t("orderInfoExpeditor.bid.changeBid")}</span>
          <button
            type="button"
            className={style.close_btn}
            onClick={handleClose}
            aria-label={t("orderInfoExpeditor.assignDriverModal.close")}
          >
            <CloseIconNew width="24" height="24" fill="#212121" />
          </button>
        </div>
        <div className={style.body}>
          <CustomInputCurrency
            value={price}
            isBgGray
            inputType="number"
            labelText={t("orderInfoExpeditor.bid.newBidLabel")}
            placeholder={t("orderInfoExpeditor.bid.yourBidPlaceholder")}
            errorText={errorText}
            currency={currency}
            lockCurrency={lockCurrency}
            onChangeValue={(value) => {
              setPrice(value);
              setErrorText("");
            }}
            onChangeCurrency={setCurrency}
          />
        </div>

        <div className={style.footer}>
          <div className={style.footer_btn}>
            <CustomButton
              text={t("common.cancel")}
              isNavy
              isWhite
              onClick={handleClose}
              disabled={isLoading}
              style={{ width: "100%" }}
            />
          </div>
          <div className={style.footer_btn}>
            <CustomButton
              text={t("orderInfoExpeditor.bid.update")}
              isNavy
              onClick={handleUpdate}
              isLoading={isLoading}
              disabled={isLoading}
              style={{ width: "100%" }}
            />
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ExpeditorUpdateBidModal;

